import { type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useMedia } from 'react-use';
import {
  ContentLayout,
  MinimalPageContent,
} from '@silvertours/common-landingpages-view';
import { Runtime } from '@silvertours/front-entities';
import {
  AppPromoModule,
  useAppPromoModuleDefaults,
  NewsletterModule,
  useNewsletterModuleDefaults,
  ContentModuleSegment,
  StageLegacy,
  ContentArticle,
  Author,
  AuthorInTitle,
  GuideLinks,
  GuideStage,
  SearchFormLegacy,
} from '@silvertours/front-features';
import { MediaLegacy } from '@silvertours/front-legacy-shared';
import { Theme, useRawFeatureTranslations } from '@silvertours/front-shared';

import { Body } from '../body';

/**
 * This is a page template that only renders the introduction and content modules.
 * Created for legal but can be used for rendering other pages where we don't
 * want to render legacy elements. To use the template, add necessary slug
 * or domainKeys in i18n/json/app/pageContent/[LOCALE].ts
 */
const MinimalPage = ({
  content: {
    article,
    introduction,
    stage,
    author,
    guidePageType,
    guideLinks,
    guideStage,
    omitStageAndSuppliers,
    textContent: { primaryHeadline },
  },
  ...commonProps
}: ContentLayout<MinimalPageContent>) => {
  const t = useTranslations('features');

  const defaultImage: MediaLegacy.ImageProps = {
    src: t('stageLegacy.stage.defaultImage.src'),
    alt: t('stageLegacy.stage.defaultImage.alt'),
    id: t('stageLegacy.stage.defaultImage.id'),
  };

  const stageImage =
    stage?.content?.imageWithFocalPoint ||
    stage?.content?.image ||
    defaultImage;
  const rawTranslations = useRawFeatureTranslations('article');

  const { isRatgeberPage, isRatgeberAzPage, isRatgeberDestinationPage } =
    Runtime.usePath();

  const translations = isRatgeberPage
    ? {
        ...rawTranslations,
        faq: { ...rawTranslations.faq, supportUrl: undefined },
      }
    : rawTranslations;
  const appPromoContent = useAppPromoModuleDefaults();
  const newsletterContent = useNewsletterModuleDefaults();

  const isXl = useMedia(`(min-width: ${Theme.getBreakpoint('xl')}px)`, false);
  const isMobile = useMedia(
    `(max-width: ${Theme.getBreakpoint('sm')}px)`,
    true,
  );

  const isSearchThin = isRatgeberPage && (isXl || isMobile);
  const searchMask = isSearchThin ? 'thin' : 'none';
  const variant = { kind: 'minimal', searchMask } as const;
  const showAuthor = !!author && guidePageType !== 'author';

  const bodyTopContent = (() => {
    if (!isSearchThin) {
      return stage ? (
        <StageLegacy.Stage
          content={stage.content}
          headline={primaryHeadline}
          variant={variant}
        />
      ) : null;
    }

    if (isMobile) {
      return (
        <StageLegacy.WizardSearchWrapper $isThin>
          <StageLegacy.SearchContainer
            image={stageImage}
            includeSearch
            isErrorPage={false}
            displayMode="thin"
          />
        </StageLegacy.WizardSearchWrapper>
      );
    }

    return <SearchFormLegacy.SearchForm displayMode="thin" isWide />;
  })();

  return (
    <Body
      variant={variant}
      title={primaryHeadline}
      author={showAuthor && <AuthorInTitle {...author} />}
      bodyTop={bodyTopContent}
      searchData={stage?.content?.search}
      content={({ container, containerModule, contentModule }) => {
        const segmented = (key: string, node: ReactNode, condition: boolean) =>
          containerModule(
            key,
            <ContentModuleSegment>{node}</ContentModuleSegment>,
            condition,
          );

        return [
          containerModule(
            'guideStage',
            guideStage && <GuideStage image={guideStage} />,
            !!guideStage,
          ),
          containerModule(
            'introduction-content',
            <ContentArticle
              modules={introduction?.segments ?? []}
              translations={translations}
              isPrimaryHeading={omitStageAndSuppliers}
            />,
            !!introduction?.segments,
          ),
          container('content-modules', [
            contentModule(
              'article',
              <ContentArticle
                modules={article.segments}
                translations={translations}
                isPrimaryHeading={omitStageAndSuppliers}
              />,
            ),
          ]),
          containerModule(
            'author',
            showAuthor && <Author {...author} />,
            showAuthor,
          ),
          segmented(
            'guideLinksModule',
            guideLinks && <GuideLinks links={guideLinks} />,
            !!guideLinks,
          ),
          segmented(
            'appPromoModule',
            <AppPromoModule {...appPromoContent} />,
            isRatgeberAzPage,
          ),
          segmented(
            'newsletterModule',
            <NewsletterModule {...newsletterContent} />,
            isRatgeberDestinationPage,
          ),
        ];
      }}
      {...commonProps}
    />
  );
};

export { MinimalPage };
